import { postDocFile, isHealthDoc } from '../../../lib/healthMask';
// Firma: eksik belgeleri tek ekranda yükleme – soldaki dosyaları sağdaki evrak başlıklarına sürükle-bırak, tek seferde yükle
import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { CheckCircle2, Upload, FileText, X, Loader2, AlertTriangle, GripVertical, Search } from 'lucide-react';
import { Input } from '../../ui/input';
import { EMPLOYEE_DOCS } from '../../employees/constants';
import { confirmDialog } from '../../ConfirmHost';
import { confirmPdfPages } from '../../../lib/pdfPageGuard';
import { PageLimitTag } from '../../documents/PageLimitTag';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_SIZE = 10 * 1024 * 1024;
const ACCEPT = /\.(pdf|jpe?g|png|webp)$/i;

// defs içindeki optional: true olanlar opsiyonel (O) sayılır
export const missingDocs = (entity, defs) => defs.filter(d => {
    const st = entity.documents?.[d.key] || 'beklemede';
    if (st === 'onay' || st === 'kapsam_disi') return false;
    return ['eksik', 'suresi_doldu'].includes(st) || !entity.document_files?.[d.key];
  });

// Zorunlu tanımı yoksa tüm belgeler zorunlu sayılır; opsiyoneller de yüklenebilir
export const missingEmployeeDocs = (emp, requiredDocs = []) => missingDocs(emp, EMPLOYEE_DOCS)
  .map(d => ({ ...d, optional: requiredDocs.length > 0 && !requiredDocs.includes(d.key) }))
  .sort((a, b) => Number(a.optional) - Number(b.optional));

// Sonraki bölüme geçişi engelleyen belge: sadece zorunlu (Z) olup yüklenmemiş veya eksik/hatalı işaretlenmiş
export const blocksEmployeeDoc = (emp, key, requiredDocs) => {
  const st = emp.documents?.[key];
  // Eksik/süresi dolmuş belge (opsiyonel olsa da) yeni sürüm yüklenip "Onay Bekliyor"a dönmeden ilerletmez
  if (['eksik', 'suresi_doldu'].includes(st)) return true;
  if (!requiredDocs.includes(key) || st === 'onay' || st === 'kapsam_disi') return false;
  return !emp.document_files?.[key];
};

export const blocksEquipmentDoc = (eq, d) => blocksEmployeeDoc(eq, d.key, d.optional ? [] : [d.key]);

export const bulkLabel = (docs) => {
  const o = docs.filter(d => d.optional).length;
  return `${docs.length - o}Z, ${o}O`;
};

let seq = 0;
// quiet: klasörden gelenlerde uygun olmayanlar sessizce atlanır
export const toItems = (fileList, quiet = false) => Array.from(fileList || []).filter(f => {
  if (!ACCEPT.test(f.name)) { if (!quiet) toast.error(`${f.name}: sadece PDF/JPG/PNG/WEBP`); return false; }
  if (f.size > MAX_SIZE) { if (!quiet) toast.error(`${f.name}: 10 MB'ı aşamaz`); return false; }
  return true;
}).map(file => ({ id: `f${++seq}`, file }));

export const FileChip = ({ item, assignedLabel, onRemove }) => (
  <div
    draggable
    onDragStart={(e) => e.dataTransfer.setData('text/plain', item.id)}
    className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs cursor-grab active:cursor-grabbing hover:border-sky-400 hover:bg-sky-50 ${assignedLabel ? 'bg-emerald-50/60 text-slate-500' : 'bg-white'}`}
    data-testid={`bulk-file-${item.id}`}
  >
    <GripVertical className="h-3.5 w-3.5 text-slate-400 shrink-0" />
    <FileText className="h-3.5 w-3.5 shrink-0" />
    <span className="flex-1 truncate" title={item.file.name}>{item.file.name}</span>
    {assignedLabel && <span className="shrink-0 text-[10px] text-emerald-700">→ {assignedLabel}</span>}
    <button type="button" onClick={onRemove} className="text-slate-400 hover:text-rose-600" data-testid={`bulk-file-remove-${item.id}`}><X className="h-3.5 w-3.5" /></button>
  </div>
);

const DocSlot = ({ d, index, file, isDone, note, onDropFile, onClear, collection }) => {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => { if (!isDone) { e.preventDefault(); setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (!isDone) onDropFile(e); }}
      className={`rounded-lg border-2 px-3 py-2 transition-colors ${isDone ? 'border-emerald-200 bg-emerald-50' : over ? 'border-sky-500 bg-sky-50' : file ? 'border-amber-400 bg-amber-50' : 'border-dashed border-slate-300 bg-white'}`}
      data-testid={`bulk-upload-row-${d.key}`}
    >
      <div className="flex items-center gap-2">
        <span className="w-5 text-xs font-semibold text-slate-500">{index + 1}.</span>
        <p className="flex-1 text-sm font-medium text-slate-800">{d.label} <span className={`ml-1 text-[10px] font-semibold ${d.optional ? 'text-emerald-700' : 'text-rose-600'}`}>{d.optional ? 'Opsiyonel' : 'Zorunlu'}</span><PageLimitTag collection={collection} docKey={d.key} className="ml-1.5" /></p>
        {isDone && <span className="flex items-center gap-1 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-4 w-4" />Yüklendi</span>}
      </div>
      {note && !isDone && <p className="ml-7 text-[11px] text-rose-600 truncate" title={note}>{note}</p>}
      {!isDone && (file ? (
        <div className="ml-7 mt-1 flex items-center gap-2 text-xs text-amber-800" data-testid={`bulk-slot-file-${d.key}`}>
          <FileText className="h-3.5 w-3.5" /><span className="flex-1 truncate">{file.file.name}</span>
          <span className="text-[10px] font-semibold">Yüklenmedi</span>
          <button type="button" onClick={onClear} className="text-slate-400 hover:text-rose-600" data-testid={`bulk-slot-clear-${d.key}`}><X className="h-3.5 w-3.5" /></button>
        </div>
      ) : <p className="ml-7 mt-1 text-[11px] text-slate-400">Dosyayı buraya sürükleyip bırakın</p>)}
    </div>
  );
};

const EntityList = ({ entities, curId, getDocs, entityName, onSelect }) => {
  const [q, setQ] = useState('');
  const list = entities.filter(e => entityName(e).toLocaleLowerCase('tr-TR').includes(q.toLocaleLowerCase('tr-TR')));
  return (
    <div className="flex min-h-0 max-h-[30vh] md:max-h-none flex-col gap-2 border-b pb-3 md:border-b-0 md:pb-0 md:border-r md:pr-3" data-testid="bulk-entity-list">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Personel ara..." className="h-8 pl-7 text-xs" data-testid="bulk-entity-search" />
      </div>
      <div className="flex-1 space-y-1 overflow-y-auto pr-1">
        {list.map(e => {
          const n = getDocs(e).length;
          return (
            <button key={e.id} type="button" onClick={() => onSelect(e.id)}
              className={`flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs transition-colors ${e.id === curId ? 'border-sky-500 bg-sky-50 font-semibold' : 'bg-white hover:bg-slate-50'}`}
              data-testid={`bulk-entity-${e.id}`}>
              <span className="flex-1 truncate">{entityName(e)}</span>
              {n ? <span className="rounded bg-rose-100 px-1.5 text-[10px] font-semibold text-rose-700">{n} eksik</span> : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
            </button>
          );
        })}
        {!list.length && <p className="py-4 text-center text-xs text-slate-400">Personel bulunamadı</p>}
      </div>
    </div>
  );
};

// entities birden fazlaysa (showList) solda personel listesi; seçilen kişinin evrakları + altta sürükle-bırak alanı
export const BulkUploadDialog = ({ entities, initialId, getDocs, entityName, onClose, onUploaded, entityType = 'employees', showList = false }) => {
  const firstId = initialId || entities.find(e => getDocs(e).length)?.id || entities[0]?.id;
  const [curId, setCurId] = useState(firstId);
  const [docs, setDocs] = useState(() => { const e = entities.find(x => x.id === firstId); return e ? getDocs(e) : []; });
  const [files, setFiles] = useState([]);
  const [assign, setAssign] = useState({});
  const [done, setDone] = useState([]);
  const [busy, setBusy] = useState(false);
  const [zoneOver, setZoneOver] = useState(false);
  const inputRef = useRef(null);
  const entity = entities.find(e => e.id === curId);

  const byId = (id) => files.find(f => f.id === id);
  const pendingKeys = Object.keys(assign).filter(k => assign[k]);
  const assignedTo = (id) => docs.find(d => assign[d.key] === id)?.label;
  const addFiles = (list) => { const items = toItems(list); setFiles(prev => [...prev, ...items]); return items; };
  const removeFile = (id) => { setFiles(prev => prev.filter(f => f.id !== id)); setAssign(prev => Object.fromEntries(Object.entries(prev).filter(([, v]) => v !== id))); };
  const dropOnSlot = (key, e) => {
    let id = e.dataTransfer.getData('text/plain');
    if (!byId(id) && e.dataTransfer.files?.length) id = addFiles([e.dataTransfer.files[0]])[0]?.id;
    if (!id) return;
    setAssign(prev => ({ ...Object.fromEntries(Object.entries(prev).filter(([, v]) => v !== id)), [key]: id }));
  };
  const selectEntity = (id) => {
    if (id === curId || busy) return;
    if (pendingKeys.length) { toast.error('Önce "Tümünü Yükle"ye basın veya eşleştirmeleri kaldırın', { description: `${pendingKeys.length} belge eşleştirildi ama yüklenmedi.` }); return; }
    const e = entities.find(x => x.id === id);
    setCurId(id); setDocs(getDocs(e)); setFiles([]); setAssign({}); setDone([]);
  };

  const uploadAll = async () => {
    setBusy(true);
    let ok = 0;
    for (const key of pendingKeys) {
      const item = byId(assign[key]);
      if (!isHealthDoc(entityType, key) && !(await confirmPdfPages(item.file, { collection: entityType, docKey: key, label: docs.find(d => d.key === key)?.label }))) continue;
      try {
        const res = await postDocFile(entityType, curId, key, item.file);
        onUploaded(curId, key, res.data);
        setDone(prev => [...prev, key]);
        setAssign(prev => ({ ...prev, [key]: undefined }));
        setFiles(prev => prev.filter(f => f.id !== item.id));
        ok += 1;
      } catch (err) {
        if (!err.cancelled) toast.error(`${docs.find(d => d.key === key)?.label}: ${err.response?.data?.detail || 'yüklenemedi'}`);
      }
    }
    setBusy(false);
    if (ok) toast.success(`${ok} belge yüklendi`);
  };

  const close = async () => {
    if (busy) return;
    if (pendingKeys.length && !(await confirmDialog({
      title: 'Eşleştirilen belgeler yüklenmedi!',
      description: `${pendingKeys.length} belge eşleştirildi ama "Tümünü Yükle"ye basılmadı. Kapatırsanız bu belgeler YÜKLENMEYECEK.`,
      confirmLabel: 'Yüklemeden Kapat',
    }))) return;
    onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent className={`${showList ? 'max-w-5xl' : 'max-w-2xl'} max-h-[90vh] flex flex-col`} data-testid="bulk-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5" />Toplu Belge Yükleme{entity ? ` – ${entityName(entity)}` : ''}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500" data-testid="bulk-upload-progress">{done.length} / {docs.length} belge yüklendi. Dosyaları aşağıya ekleyin, ilgili evrak başlığına sürükleyin, sonra "Tümünü Yükle"ye basın.</p>
        <div className={`grid flex-1 min-h-0 gap-3 ${showList ? 'grid-cols-1 md:grid-cols-[230px_1fr]' : 'grid-cols-1'}`}>
          {showList && <EntityList entities={entities} curId={curId} getDocs={getDocs} entityName={entityName} onSelect={selectEntity} />}
          <div className="min-h-0 space-y-3 overflow-y-auto pr-1">
            <div className="space-y-2">
              {docs.map((d, i) => (
                <DocSlot key={d.key} d={d} index={i} collection={entityType} file={assign[d.key] && byId(assign[d.key])} isDone={done.includes(d.key)}
                  note={entity?.documents?.[`${d.key}_note`]} onDropFile={(e) => dropOnSlot(d.key, e)}
                  onClear={() => setAssign(prev => ({ ...prev, [d.key]: undefined }))} />
              ))}
              {!docs.length && <p className="py-6 text-center text-sm text-emerald-700" data-testid="bulk-no-missing">Bu kaydın eksik belgesi yok.</p>}
            </div>
            {docs.length > 0 && (
              <div className="sticky bottom-0 space-y-2 border-t bg-white pt-3">
                <div
                  onDragOver={(e) => { e.preventDefault(); setZoneOver(true); }}
                  onDragLeave={() => setZoneOver(false)}
                  onDrop={(e) => { e.preventDefault(); setZoneOver(false); addFiles(e.dataTransfer.files); }}
                  onClick={() => inputRef.current?.click()}
                  className={`cursor-pointer rounded-lg border-2 border-dashed p-3 text-center transition-colors ${zoneOver ? 'border-sky-500 bg-sky-50' : 'border-slate-300 bg-slate-50 hover:bg-slate-100'}`}
                  data-testid="bulk-file-dropzone"
                >
                  <p className="flex items-center justify-center gap-2 text-sm font-medium text-slate-700"><Upload className="h-4 w-4 text-slate-400" />Dosyaları buraya sürükleyin veya tıklayıp seçin</p>
                  <p className="text-[11px] text-slate-500">Birden fazla dosya · PDF, JPG, PNG, WEBP · en fazla 10 MB</p>
                  <input ref={inputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="bulk-file-input" />
                </div>
                {files.length > 0 && (
                  <div className="grid max-h-36 grid-cols-1 gap-1.5 overflow-y-auto sm:grid-cols-2" data-testid="bulk-file-list">
                    {files.map(f => <FileChip key={f.id} item={f} assignedLabel={assignedTo(f.id)} onRemove={() => removeFile(f.id)} />)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {pendingKeys.length > 0 && (
          <div className="flex items-center gap-2 rounded-lg border-2 border-rose-500 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 animate-pulse" data-testid="bulk-upload-pending-warning">
            <AlertTriangle className="h-4 w-4 shrink-0" />{pendingKeys.length} belge eşleştirildi ama henüz YÜKLENMEDİ. "Tümünü Yükle"ye basın!
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={busy} data-testid="bulk-upload-close">Kapat</Button>
          <Button onClick={uploadAll} disabled={!pendingKeys.length || busy} className={`bg-rose-600 text-white hover:bg-rose-700 ${pendingKeys.length ? 'upload-glow' : ''}`} data-testid="bulk-upload-all-btn">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />}Tümünü Yükle{pendingKeys.length ? ` (${pendingKeys.length})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
